import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { InputSwitch } from '@creditornot/cb-switch';
import union from '@turf/union';
import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { GeoRange, DeliveryAreaValue } from 'apis/delivery-settings-api/types';
import { WoltModal } from 'common/components';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import validateYupSchema from 'common/utils/validateYupSchema';

import DeliveryAreasTable from './DeliveryAreasTable';
import DeliveryFee from './DeliveryFee';

type DeliveryAreaFormValues = { deliveryAreas: DeliveryAreaValue[] };

type DeliveryAreasFormProps = {
  deliveryAreas: DeliveryAreaValue[];
  currency: Currency;
  isSubmitting: boolean;
  onUpdate: (id: string, disabled: boolean) => void;
  onSubmit: (data: DeliveryAreaFormValues) => void;
  onCancel: () => void;
};

const FormButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const CancelButton = styled(Button)``;

const SaveButton = styled(Button)``;

const StyledAlert = styled(Alert)`
  margin-block-start: 8px;
`;

const AreasTable = styled(DeliveryAreasTable)`
  margin-block-start: 16px;
`;

const StyledWoltModal = styled(WoltModal)`
  width: 400px;
  background: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
`;

const StyledModalFooter = styled(ModalFooter)`
  justify-content: flex-end;
`;

const deliveryAreaValidationSchema: yup.SchemaOf<{
  deliveryAreas: Omit<DeliveryAreaValue, 'labelPosition' | 'pricingCircles'>[];
}> = yup
  .object({
    deliveryAreas: yup.array(
      yup.object({
        id: yup.string().required(),
        name: yup.string().required(),
        geoRange: yup.mixed(),
        deliveryBaseFee: yup.number(),
        distanceBasedPricing: yup.array(yup.mixed()),
        minBasketSize: yup.number(),
        disabled: yup.boolean().required(),
      }),
    ),
  })
  .test('combined-delivery-areas', 'Invalid areas', ({ deliveryAreas }, { createError }) => {
    if (!deliveryAreas || !deliveryAreas.length) {
      return true;
    }
    const enabledAreas = deliveryAreas.filter(area => !area.disabled);
    if (enabledAreas.length < 1) {
      return createError({ message: 'venue-details.delivery-areas.validation-one-area-enabled' });
    }
    if (enabledAreas.length === deliveryAreas.length || enabledAreas.length === 1) {
      return true;
    }
    // Check that the enabled areas form together a continuous area
    // Extract the coordinates separated into two groups for calculation
    const [firstArea, ...otherAreas] = enabledAreas.map(area =>
      (area.geoRange as GeoRange)?.coordinates.map(coords => coords.map(coord => [coord.longitude, coord.latitude])),
    );
    const combinedArea = union(
      { type: 'Polygon', coordinates: firstArea },
      { type: 'MultiPolygon', coordinates: otherAreas },
    );
    if (combinedArea?.geometry.type === 'MultiPolygon') {
      return createError({ message: 'venue-details.delivery-areas.validation-enabled-areas-connected' });
    }
    return true;
  });

const DeliveryAreasForm = ({
  deliveryAreas,
  currency,
  isSubmitting,
  onUpdate,
  onSubmit,
  onCancel,
}: DeliveryAreasFormProps) => {
  // Instead of submitting directly, show a modal telling extra info and only submit when the modal is closed
  const [dataToSubmit, setDataToSubmit] = useState<DeliveryAreaFormValues>();
  const submitData = () => {
    if (dataToSubmit) {
      onSubmit(dataToSubmit);
      setDataToSubmit(undefined);
    }
  };

  return (
    <>
      <Form<DeliveryAreaFormValues>
        onSubmit={setDataToSubmit}
        initialValues={{ deliveryAreas }}
        validate={validateYupSchema(deliveryAreaValidationSchema)}
      >
        {({ values, hasValidationErrors, error, handleSubmit, pristine }) => (
          <>
            <FormButtons>
              <CancelButton variant="lightBlue" size="small" onClick={onCancel} disabled={isSubmitting}>
                <FormattedMessage id="state.cancel" />
              </CancelButton>
              <SaveButton
                variant="blue"
                size="small"
                disabled={pristine || isSubmitting || hasValidationErrors}
                onClick={handleSubmit}
              >
                <FormattedMessage id="state.save" />
              </SaveButton>
            </FormButtons>

            {hasValidationErrors && !!error && typeof error === 'string' && (
              <StyledAlert variant="error" size="small">
                <FormattedMessage id={error} />
              </StyledAlert>
            )}

            <AreasTable
              rows={values.deliveryAreas.map((area, index) => ({
                areaName: area.name,
                isDisabledArea: area.disabled,
                deliveryFee: (
                  <DeliveryFee
                    currency={currency}
                    deliveryBaseFee={area.deliveryBaseFee}
                    distanceBasedPricing={area.distanceBasedPricing}
                  />
                ),
                minBasketSize:
                  area.minBasketSize === undefined ? (
                    '-'
                  ) : (
                    <MonetaryAmount
                      amount={convertMonetaryIntToDecimal(area.minBasketSize, currency)}
                      currency={currency}
                    />
                  ),
                enabled: (
                  <Field<boolean> name={`deliveryAreas[${index}].disabled`} type="checkbox">
                    {({ input }) => (
                      <InputSwitch
                        checked={!input.checked}
                        onChange={e => {
                          input.onChange(!e.target.checked);
                          onUpdate(area.id, !e.target.checked);
                        }}
                      />
                    )}
                  </Field>
                ),
              }))}
            />
          </>
        )}
      </Form>
      {dataToSubmit && (
        <StyledWoltModal isOpen onRequestClose={submitData}>
          <ModalHeader onCloseClick={submitData}>
            <FormattedMessage id="venue-details.delivery-areas.toggling-areas-info-popup.title" />
          </ModalHeader>
          <ModalContent>
            <FormattedMessage id="venue-details.delivery-areas.toggling-areas-info-popup.description" />
          </ModalContent>
          <StyledModalFooter>
            <Button onClick={submitData} variant="blue" size="small">
              <FormattedMessage id="venue-details.delivery-areas.toggling-areas-info-popup.ok" />
            </Button>
          </StyledModalFooter>
        </StyledWoltModal>
      )}
    </>
  );
};

export default DeliveryAreasForm;
